// [2026-08-07] ADDED — the single place this app talks to the dashboard API.
//
// SAME-ORIGIN, ALWAYS. The built app is served by app/admin_server.py itself (index.html at "/",
// assets under "/static"), so every request below is a relative path. The session token therefore
// never crosses an origin boundary and no CORS configuration is involved — which is exactly why
// the dashboard was built as a second FastAPI app rather than a separate site.
//
// WHY THE TOKEN IS IN sessionStorage AND NOT localStorage.
// sessionStorage is scoped to the tab and cleared when it closes. A dashboard session grants access
// to every customer's usage and credit data; leaving it readable on a shared machine after the
// browser closes is a worse trade than making someone sign in again.

const BASE = '/admin/v1'
const TOKEN_KEY = 'pg.session'

export function getToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) } catch { return null }
}

export function setToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token)
    else sessionStorage.removeItem(TOKEN_KEY)
  } catch { /* private browsing — the app still works, it just will not persist across reloads */ }
}

// [2026-09-02] ADDED — the PLATFORM (SSO) surface, additive and separate from everything above.
//
// WHY A SECOND SURFACE AND NOT A CHANGE TO `request`/`api`.
// The methods above talk to the OLD gateway dashboard API at BASE='/admin/v1' with a Bearer session
// this app minted from an email/password login. proctoring-frontend-admin imports `api`, `request`'s
// callers and `getToken/setToken` and compiles against them — src/index.js calls this file a
// cross-repo contract. So NONE of that is touched. Instead the customer app now signs in through the
// EXISTING InCruiter account (no password form) and talks to the deployed proctoring-sdk-backend's
// `/me` endpoints. Those live at the app ROOT (not under /admin/v1) and are authenticated by the
// account cookie, so they need their own request path.
//
// SAME-ORIGIN BY DEFAULT (PLATFORM_BASE=''). In production the customer build is served by the SDK
// backend on a *.incruiter.com host, so a relative `/me` is same-origin and the account cookie rides
// along with no CORS. In `npm run dev` vite proxies `/me` to the backend (see vite.config.js). Set
// VITE_SDK_API only to point the browser at an ABSOLUTE backend host (needs CORS there); leave it
// unset for the same-origin path this product is built around.
const PLATFORM_BASE = (import.meta.env?.VITE_SDK_API || '').replace(/\/+$/, '')

// A pasted token_dev, used ONLY on localhost where the .incruiter.com cookie is not sent to the dev
// server. On a real subdomain deploy this stays empty and the cookie is used instead. sessionStorage,
// not localStorage, for the same reason as the session token above: tab-scoped, gone on close.
const PLATFORM_TOKEN_KEY = 'proctor.platform_token'
export function getPlatformToken() {
  try { return sessionStorage.getItem(PLATFORM_TOKEN_KEY) } catch { return null }
}
export function setPlatformToken(token) {
  try {
    if (token) sessionStorage.setItem(PLATFORM_TOKEN_KEY, token)
    else sessionStorage.removeItem(PLATFORM_TOKEN_KEY)
  } catch { /* private browsing — still works, just not across reloads */ }
}

async function platformRequest(path, { method = 'GET', body, signal } = {}) {
  const headers = {}
  const token = getPlatformToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body) headers['Content-Type'] = 'application/json'

  let response
  try {
    response = await fetch(PLATFORM_BASE + path, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      // Send the account cookie on a same-origin / subdomain deploy. Harmless on localhost, where
      // there is no such cookie and the pasted token above is used instead.
      credentials: 'include',
      signal,
    })
  } catch (err) {
    if (err.name === 'AbortError') throw err
    throw new ApiError('Could not reach the proctoring service.', { code: 'NETWORK' })
  }

  let payload = null
  const text = await response.text()
  if (text) { try { payload = JSON.parse(text) } catch { /* non-JSON, handled below */ } }

  // 401 and 503 carry DISTINCT meanings here and the login screen branches on them: 401 = not signed
  // in (show the "open from your InCruiter account" screen), 503 = the feature is not enabled on this
  // deployment yet (a different message, and not the user's fault). See app/modules/me/route.py.
  if (response.status === 401) {
    throw new ApiError(payload?.error?.message
      || 'Sign in to your InCruiter account to open the proctoring dashboard.',
      { code: 'NOT_AUTHENTICATED', status: 401 })
  }
  if (response.status === 503) {
    throw new ApiError(payload?.error?.message
      || 'The proctoring dashboard is not enabled on this deployment yet.',
      { code: 'FEATURE_OFF', status: 503 })
  }
  if (!response.ok) {
    const error = payload?.error || {}
    throw new ApiError(error.message || `Request failed (${response.status}).`,
                       { code: error.code, status: response.status, details: error.details })
  }
  // Every /me response is the standard envelope { success, request_id, data }. Callers want `data`.
  return payload?.data ?? payload
}

// The customer self-service surface, keyed on the SIGNED-IN account (not an API key). The client_id
// is resolved server-side from the session on every call — the browser never names a company.
export const platform = {
  me: () => platformRequest('/me'),
  keys: () => platformRequest('/me/keys'),
  // [2026-09-06] CHANGED — takes the SERVICE too. A customer holds two credentials that are
  // identical in format and differ only by this field: 'sdk' for the browser SDK on their exam
  // page, 'proctoring' for their backend calling the proctoring API. Omitting it defaults to 'sdk'
  // server-side (app/core/apikeys.py), which is why every key minted here used to be an SDK key.
  // The accepted values are exactly ['sdk', 'proctoring'] — the UI calls the second one an
  // "API key", but that is a label, not the wire value.
  // OLD: createKey: (label) => platformRequest('/me/keys', { method: 'POST', body: { label: label || '' } }),
  createKey: (label, service = 'sdk') =>
    platformRequest('/me/keys', { method: 'POST', body: { label: label || '', service } }),
  // Revoke a key by its full value OR its display prefix (both accepted server-side).
  revokeKey: (apiKey, reason) =>
    platformRequest('/me/keys/revoke',
      { method: 'POST', body: { api_key: apiKey, reason: reason || 'rotated by owner' } }),
  // [2026-09-03] ADDED — remove a REVOKED key's row. Housekeeping, not a security action: the
  // server refuses an active key, so a live credential cannot be made to vanish in one step. Revoke
  // (which is logged with who, when and why) has to happen first.
  deleteKey: (apiKey) =>
    platformRequest('/me/keys/delete', { method: 'POST', body: { api_key: apiKey } }),
  credits: (limit) => platformRequest(`/me/credits${qs({ limit })}`),

  // [2026-09-02] ADDED — the analytics, usage and report surfaces.
  //
  // `analytics` is ONE call that returns every panel on the screen (totals, traffic series, latency
  // percentiles + trend, hour-of-day, heatmap, endpoints, sessions series, event breakdown). One
  // round trip rather than nine, so the screen never renders half-populated while the rest are
  // still in flight — the state that makes a working dashboard look broken.
  analytics: (days) => platformRequest(`/me/analytics${qs({ days })}`),

  // Paginated + searchable tables. `total` comes back with every page, counted with the SAME
  // filters, so a "showing 1-50 of N" line can never disagree with the rows under it.
  requests: (params) => platformRequest(`/me/requests${qs(params)}`),
  sessions: (params) => platformRequest(`/me/sessions${qs(params)}`),
  session: (id) => platformRequest(`/me/sessions/${encodeURIComponent(id)}`),

  // [2026-09-06] ADDED — the analysis API's submitted jobs, and one job in full.
  //
  // These read the OWNERSHIP ROWS this service writes at submission, not the analysis engine — the
  // engine cannot enumerate a client's jobs and does not know who owns one. So a row means "the
  // engine accepted this work", never "the analysis finished": reports are delivered to a webhook
  // and no callback is sent when one fails.
  videoJobs: (params) => platformRequest(`/me/video-jobs${qs(params)}`),
  videoJob: (id) => platformRequest(`/me/video-jobs/${encodeURIComponent(id)}`),
  // The finished findings WITHOUT the proof images — ~12 KB, against 4.3 MB for the whole report.
  // Each interval carries `has_proof`; the image itself is fetched per-interval by the helper
  // below, the same shape the browser-SDK evidence frames already use.
  videoReport: (id) => platformRequest(`/me/video-jobs/${encodeURIComponent(id)}/report`),

  // [2026-09-03] ADDED — team members. Who at this company may open the dashboard, and what they
  // may do. The list also returns `my_role` and `my_email` so the screen can hide controls this
  // person cannot use — that is COSMETIC ONLY. Every one of these is authorised server-side in
  // app/modules/me/route.py; hiding a button is politeness, not a permission check.
  // [2026-09-05] ADDED — the InCruiter product list for the shell's product rail, proxied by
  // GET /me/products. It is proxied rather than fetched from the browser because the subscription
  // system answers with Access-Control-Allow-Origin: *, which forbids credentials — this app
  // authenticates by the account COOKIE on a real deploy and holds no bearer token there.
  //
  // The response is { products: [...], configured: bool }. `configured` distinguishes "no product
  // URL wired on this deployment" from "wired and returned nothing", so the rail can stay quiet in
  // the first case instead of looking broken.
  products: () => platformRequest('/me/products'),

  members: () => platformRequest('/me/members'),
  inviteMember: (email, role) =>
    platformRequest('/me/members/invite', { method: 'POST', body: { email, role } }),
  setMemberRole: (email, role) =>
    platformRequest('/me/members/role', { method: 'POST', body: { email, role } }),
  revokeMember: (email) =>
    platformRequest('/me/members/revoke', { method: 'POST', body: { email, role: 'viewer' } }),

  // Redeem an invitation. The token comes from the URL FRAGMENT, never a query string — see
  // AcceptInvite.jsx for why that matters and why it is stripped immediately after reading.
  acceptInvite: (token) =>
    platformRequest('/me/invites/accept', { method: 'POST', body: { token } }),

  // [2026-11-02] ADDED — INC-2082: What's New, proxied by the SDK backend for the same reason as
  // `products` above: the service behind it wants a bearer token, and on a real deploy this app has
  // only the account cookie. Both answer softly, so a failure here can only hide the button.
  whatsNew: (productId) => platformRequest(`/me/whats-new${qs({ product_id: productId })}`),
  markWhatsNewRead: (announcementId, productId) =>
    platformRequest(`/me/whats-new/${encodeURIComponent(announcementId)}/read`,
                    { method: 'POST', body: { product_id: productId } }),
}

// [2026-09-03] ADDED — fetch ONE evidence screenshot for the signed-in customer, as an object URL.
//
// WHY NOT JUST PUT THE URL IN <img src>. Two independent reasons, either one sufficient:
//   1. On localhost and on any host that is not *.incruiter.com, this app authenticates with a
//      PASTED BEARER TOKEN, and an <img> tag cannot send an Authorization header. The image would
//      simply 401 and render as a broken-image icon.
//   2. Making the URL work without a header would mean an unauthenticated link to a screenshot of
//      a candidate's screen — a link that works for anyone who ever sees it.
// So the bytes are fetched with the same credentials as every other call and wrapped in a blob URL.
//
// `credentials: 'include'` is what makes the cookie path work CROSS-ORIGIN, which is the shape this
// app is deployed in now (its own host, talking to the SDK backend). It is also why the backend
// cannot answer with a wildcard CORS origin.
//
// THE CALLER MUST REVOKE IT. Each object URL pins its decoded image in memory until
// revokeObjectURL is called; a session with sixty frames would otherwise hold sixty decoded JPEGs
// for the life of the page. EvidenceFrame does this in its effect cleanup — mirroring the same
// contract as evidenceObjectUrl/browserEvidenceObjectUrl below, which serve the OLD gateway API.
export async function platformEvidenceObjectUrl(sessionId, eventId, signal) {
  const headers = {}
  const token = getPlatformToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(
    `${PLATFORM_BASE}/me/sessions/${encodeURIComponent(sessionId)}/evidence/${encodeURIComponent(eventId)}`,
    { headers, credentials: 'include', signal },
  )
  if (!response.ok) throw new Error(`Evidence unavailable (${response.status})`)
  return URL.createObjectURL(await response.blob())
}

/** ONE proof image from an analysis report, as an object URL.
 *
 *  Same contract as platformEvidenceObjectUrl above, and the SAME caller obligation: the returned
 *  URL holds a blob in memory until it is revoked, so a component that fetches one must
 *  URL.revokeObjectURL it on unmount or a reviewer paging through a report leaks every frame they
 *  looked at.
 */
export async function platformProofObjectUrl(jobId, kind, index, signal) {
  const headers = {}
  const token = getPlatformToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(
    `${PLATFORM_BASE}/me/video-jobs/${encodeURIComponent(jobId)}`
    + `/proof/${encodeURIComponent(kind)}/${encodeURIComponent(index)}`,
    { headers, credentials: 'include', signal },
  )
  if (!response.ok) throw new Error(`Proof image unavailable (${response.status})`)
  return URL.createObjectURL(await response.blob())
}

/** Thrown for every non-2xx. Carries the API's stable `code` so callers can branch on it. */
export class ApiError extends Error {
  constructor(message, { code, status, details } = {}) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }
}

async function request(path, { method = 'GET', body, isForm = false, signal } = {}) {
  const headers = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body && !isForm) headers['Content-Type'] = 'application/json'

  let response
  try {
    response = await fetch(BASE + path, {
      method,
      headers,
      body: isForm ? body : (body ? JSON.stringify(body) : undefined),
      signal,
    })
  } catch (err) {
    // A dead network and a 500 are different problems with different fixes, so they get different
    // messages. "Failed to fetch" on its own sends people to check the wrong thing.
    if (err.name === 'AbortError') throw err
    throw new ApiError('Could not reach the dashboard API. Is the admin server running?',
                       { code: 'NETWORK' })
  }

  if (response.status === 401) {
    // The session is gone or expired. Clear it so the shell drops straight to the login screen
    // instead of every panel rendering its own "unauthorised" error.
    setToken(null)
    throw new ApiError('Your session has expired. Please sign in again.',
                       { code: 'UNAUTHORISED', status: 401 })
  }

  let payload = null
  const text = await response.text()
  if (text) { try { payload = JSON.parse(text) } catch { /* non-JSON body, handled below */ } }

  if (!response.ok) {
    const error = payload?.error || {}
    throw new ApiError(error.message || `Request failed (${response.status}).`,
                       { code: error.code, status: response.status, details: error.details })
  }
  return payload
}

const qs = (params) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params || {})) {
    if (value !== undefined && value !== null && value !== '') search.set(key, value)
  }
  const encoded = search.toString()
  return encoded ? `?${encoded}` : ''
}

export const api = {
  // ── auth ────────────────────────────────────────────────────────────────
  // [2026-08-27] CHANGED — every auth call now carries the DOOR it came from.
  //
  // OLD:
  //   login: (email, password) => request('/auth/login', {method:'POST', body:{email, password}}),
  //   providers: () => request('/auth/providers'),
  //   googleLogin: (credential) => request('/auth/google', {method:'POST', body:{credential}}),
  //
  // The customer sign-in lives at `/` and the staff sign-in at `/admin`, and the server refuses a
  // credential presented at the wrong one (`_enforce_door` in app/modules/admin/route.py). Sending
  // the door is what lets it do that. `door` is optional server-side, so an older client still
  // works — but this app always sends it.
  login: (email, password, door) =>
    request('/auth/login', { method: 'POST', body: { email, password, door } }),
  // Public — called before sign-in to learn which methods this deployment offers. Returns only
  // the Google client id, which is public by design; no secret crosses the wire.
  // Answers PER DOOR: the staff page is told Google is unavailable, so it never renders a button
  // the server would refuse.
  providers: (door) => request(`/auth/providers${qs({ door })}`),
  googleLogin: (credential, door) =>
    request('/auth/google', { method: 'POST', body: { credential, door } }),
  // [2026-08-27] ADDED — client self-registration and password reset. Both are CLIENT-door only;
  // the staff page never calls them and the server refuses them for staff accounts regardless.
  googleSignup: (credential, company) =>
    request('/auth/signup/google', { method: 'POST', body: { credential, company } }),
  forgotPassword: (email) =>
    request('/auth/password/forgot', { method: 'POST', body: { email } }),
  resetPassword: (token, password) =>
    request('/auth/password/reset', { method: 'POST', body: { token, password } }),
  me: () => request('/auth/me'),

  // ── client-scoped ───────────────────────────────────────────────────────
  overview: (days) => request(`/me/overview${qs({ days })}`),
  usage: (days, endpoint) => request(`/me/usage${qs({ days, endpoint })}`),
  events: (params) => request(`/me/usage/events${qs(params)}`),
  // The analytics panels — latency percentiles, hour-of-day, heatmap — in ONE round trip, so a
  // screen never renders half-populated while five other requests are still in flight.
  analytics: (days, endpoint) => request(`/me/analytics${qs({ days, endpoint })}`),
  // Analytics about the PROCTORING RESULTS (findings, scores, clean rate) rather than about
  // API traffic. Read from stored job summaries, so it always agrees with the reports.
  proctoringAnalytics: () => request('/me/analytics/proctoring'),
  credits: (params) => request(`/me/credits${qs(params)}`),
  // [2026-08-11] ADDED — buying credits. NOTE WHAT createOrder DOES NOT SEND: any amount, price
  // or credit count. It sends a pack KEY and the server looks up what that costs. Adding an
  // amount here would hand the price to the browser, which is the single most common way a
  // Razorpay integration gets robbed. See app/core/payments.py.
  paymentsConfig: () => request('/me/payments/config'),
  createOrder: (pack) => request('/me/payments/orders', { method: 'POST', body: { pack } }),
  mockPay: (orderId) =>
    request(`/me/payments/mock/${encodeURIComponent(orderId)}/pay`, { method: 'POST' }),
  verifyPayment: (body) => request('/me/payments/verify', { method: 'POST', body }),
  abandonOrder: (orderId) =>
    request(`/me/payments/${encodeURIComponent(orderId)}/abandon`, { method: 'POST' }),
  paymentOrders: (limit) => request(`/me/payments/orders${qs({ limit })}`),
  estimate: (body) => request('/me/credits/estimate', { method: 'POST', body }),
  keys: () => request('/me/keys'),
  // [2026-08-10] ADDED — self-service credentials. `createKey` is the ONLY call in this file whose
  // response contains a live secret; it is rendered once and never stored by the app (see
  // views/Keys.jsx). Do not add it to any cache, retry or logging wrapper.
  createKey: (body) => request('/me/keys', { method: 'POST', body }),
  revokeKey: (apiKey) => request('/me/keys/revoke', { method: 'POST', body: { api_key: apiKey } }),
  services: () => request('/me/services'),
  sessions: (limit) => request(`/me/sessions${qs({ limit })}`),
  session: (jobId) => request(`/me/sessions/${encodeURIComponent(jobId)}`),
  upload: (formData) => request('/me/analyze/upload', { method: 'POST', body: formData, isForm: true }),

  // [2026-08-10] ADDED — candidate reports from the browser SDK. Distinct from `sessions` above,
  // which are recorded-VIDEO analysis jobs. The two are joined server-side on reference_id, so
  // `browserSession` returns the browser record AND any video analyses of the same candidate.
  browserSessions: (params) => request(`/me/browser-sessions${qs(params)}`),
  browserSession: (id) => request(`/me/browser-sessions/${encodeURIComponent(id)}`),
  browserAnalytics: (days) => request(`/me/browser-analytics${qs({ days })}`),

  // [2026-08-11] ADDED — the UNIFIED candidate report, keyed on reference_id.
  //
  // reference_id, not job_id or session_id: a job identifies a recording and a session identifies
  // a browser tab, but a reviewer is deciding about a PERSON. reference_id is the customer's own
  // id for that person and is the only key under which both halves of the product can be joined.
  candidateReport: (referenceId) =>
    request(`/me/candidates/${encodeURIComponent(referenceId)}/report`),
  addFeedback: (referenceId, body) =>
    request(`/me/candidates/${encodeURIComponent(referenceId)}/feedback`,
            { method: 'POST', body }),
  // The response to this is the ONLY time the share token is served. Never cache or log it.
  createShare: (referenceId, body) =>
    request(`/me/candidates/${encodeURIComponent(referenceId)}/share`, { method: 'POST', body }),
  revokeShare: (referenceId, shareId) =>
    request(`/me/candidates/${encodeURIComponent(referenceId)}/share/${encodeURIComponent(shareId)}/revoke`,
            { method: 'POST' }),

  // ── operator ────────────────────────────────────────────────────────────
  // [2026-08-11] ADDED — operator system health, and API key management on a client's behalf.
  // All four are require_admin() server-side; hiding the nav from a customer is convenience only.
  adminHealth: (days) => request(`/admin/health${qs({ days })}`),
  adminClientKeys: (clientId) => request(`/admin/clients/${encodeURIComponent(clientId)}/keys`),
  adminCreateKey: (clientId, body) =>
    request(`/admin/clients/${encodeURIComponent(clientId)}/keys`, { method: 'POST', body }),
  adminRevokeKey: (clientId, apiKey) =>
    request(`/admin/clients/${encodeURIComponent(clientId)}/keys/revoke`,
            { method: 'POST', body: { api_key: apiKey } }),

  adminOverview: (days) => request(`/admin/overview${qs({ days })}`),
  adminAnalytics: (days, clientId) => request(`/admin/analytics${qs({ days, client_id: clientId })}`),
  adminProctoringAnalytics: (clientId) => request(`/admin/analytics/proctoring${qs({ client_id: clientId })}`),
  adminClients: (days) => request(`/admin/clients${qs({ days })}`),
  adminSessions: (params) => request(`/admin/sessions${qs(params)}`),
  adminAudit: (params) => request(`/admin/audit${qs(params)}`),
  adminClientServices: (id) => request(`/admin/clients/${encodeURIComponent(id)}/services`),
  adminToggleService: (id, key, enabled) =>
    request(`/admin/clients/${encodeURIComponent(id)}/services/${encodeURIComponent(key)}`,
            { method: 'PATCH', body: { enabled } }),
  adminGrant: (id, body) =>
    request(`/admin/clients/${encodeURIComponent(id)}/credits/grant`, { method: 'POST', body }),
  // [2026-08-27] ADDED — activate or suspend a tenant.
  //
  // The last step of the self-registration flow: a customer signs up, their tenant is created
  // DISABLED so it can call nothing, and an operator switches it on here after granting credits
  // and issuing a key. Without this the signup path had no completion.
  //
  // Exported from THIS package because it is part of the api contract the admin app imports —
  // see the note at the top of src/index.js about this file being a cross-repo contract.
  adminSetClientEnabled: (id, enabled, reason) =>
    request(`/admin/clients/${encodeURIComponent(id)}/enabled`,
            { method: 'PATCH', body: { enabled, reason } }),
  adminClientCredits: (id, limit) =>
    request(`/admin/clients/${encodeURIComponent(id)}/credits${qs({ limit })}`),
  adminUsers: () => request('/admin/users'),
  adminCreateUser: (body) => request('/admin/users', { method: 'POST', body }),
  // [2026-08-10] ADDED — cross-client candidate rollups. The LIST is deliberately not duplicated
  // here: /me/browser-sessions already returns every client's sessions for an operator, so a second
  // admin-only list endpoint would be a second place for the tenant rules to drift.
  adminBrowserAnalytics: (days, clientId) =>
    request(`/admin/browser-analytics${qs({ days, client_id: clientId })}`),

  // ── system ──────────────────────────────────────────────────────────────
  storesHealth: () => request('/health/stores'),
}

// [2026-08-07] ADDED — fetch one finding's evidence screenshot as an object URL.
//
// WHY NOT JUST PUT THE URL IN <img src>: the dashboard authenticates with a bearer token, and an
// <img> tag cannot send an Authorization header. The alternative — making the image URL work
// without authentication — would be a link to a screenshot of a candidate's screen that works for
// anyone who ever sees it. So the bytes are fetched with the token and wrapped in a blob URL.
//
// THE CALLER MUST REVOKE IT. Each object URL pins its blob in memory until revokeObjectURL is
// called; a report with eighty findings would otherwise hold eighty decoded JPEGs for the life of
// the page. EvidenceImage does this in its effect cleanup.
export async function evidenceObjectUrl(jobId, logId, signal) {
  const token = getToken()
  const response = await fetch(`${BASE}/me/sessions/${encodeURIComponent(jobId)}/evidence/${encodeURIComponent(logId)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  })
  if (!response.ok) throw new Error(`Evidence unavailable (${response.status})`)
  return URL.createObjectURL(await response.blob())
}

// [2026-08-10] ADDED — the same trick for a browser-SDK capture. Separate function rather than a
// parameter on the one above, because they are different endpoints on different records and a
// single function taking a "kind" flag would be one typo away from asking the wrong store for a
// candidate's screenshot. Callers must revoke the URL — see the note above.
export async function browserEvidenceObjectUrl(sessionId, eventId, signal) {
  const token = getToken()
  const response = await fetch(
    `${BASE}/me/browser-sessions/${encodeURIComponent(sessionId)}/evidence/${encodeURIComponent(eventId)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal },
  )
  if (!response.ok) throw new Error(`Evidence unavailable (${response.status})`)
  return URL.createObjectURL(await response.blob())
}
